import { h, createContext } from "preact";
import { useState, useContext, useMemo, useEffect } from "preact/hooks";

const LocationFormContext = createContext();
const STORAGE_KEY = "locationFormState";
const DEFAULT_VALUES = {
  location: "",
  geoItem: null,
  bookingFrom: null,
  bookingTo: null,
  adults: 1,
  kids: 0,
  dogs: 0,
};

const hasValue = (value) => value !== undefined && value !== null;

const resolveInitialValues = (initialValues = {}) => {
  const storedValues = window.localStorage.getItem(STORAGE_KEY)
    ? JSON.parse(window.localStorage.getItem(STORAGE_KEY))
    : {};

  return {
    location: hasValue(initialValues.location)
      ? initialValues.location
      : hasValue(storedValues.location)
        ? storedValues.location
        : DEFAULT_VALUES.location,
    geoItem: hasValue(initialValues.geoItem)
      ? initialValues.geoItem
      : hasValue(storedValues.geoItem)
        ? storedValues.geoItem
        : DEFAULT_VALUES.geoItem,
    bookingFrom:
      hasValue(storedValues.bookingFrom) &&
      new Date(storedValues.bookingFrom) > new Date()
        ? new Date(storedValues.bookingFrom)
        : DEFAULT_VALUES.bookingFrom,
    bookingTo:
      hasValue(storedValues.bookingTo) &&
      new Date(storedValues.bookingTo) > new Date()
        ? new Date(storedValues.bookingTo)
        : DEFAULT_VALUES.bookingTo,
    adults: hasValue(storedValues.adults)
      ? storedValues.adults
      : DEFAULT_VALUES.adults,
    kids: hasValue(storedValues.kids) ? storedValues.kids : DEFAULT_VALUES.kids,
    dogs: hasValue(storedValues.dogs) ? storedValues.dogs : DEFAULT_VALUES.dogs,
  };
};

export function LocationFormProvider({ children, initialValues = {} }) {
  const resolvedInitialValues = useMemo(
    () => resolveInitialValues(initialValues),
    [initialValues]
  );

  const [location, setLocation] = useState(resolvedInitialValues.location);
  const [geoItem, setGeoItem] = useState(resolvedInitialValues.geoItem);
  const [bookingFrom, setBookingFrom] = useState(
    resolvedInitialValues.bookingFrom
  );
  const [bookingTo, setBookingTo] = useState(resolvedInitialValues.bookingTo);
  const [adults, setAdults] = useState(resolvedInitialValues.adults);
  const [kids, setKids] = useState(resolvedInitialValues.kids);
  const [dogs, setDogs] = useState(resolvedInitialValues.dogs);

  const [showAutocompleteOverlay, setShowAutocompleteOverlay] = useState(false);
  const [showGuestOverlay, setShowGuestOverlay] = useState(false);

  const isValid = useMemo(() => {
    return geoItem && adults;
  }, [geoItem, adults]);

  // Persist data in localstorage
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const stateToPersist = {
      location,
      geoItem,
      bookingFrom,
      bookingTo,
      adults,
      kids,
      dogs,
    };

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToPersist));
    } catch (error) {
      // Ignore storage write failures (e.g., private mode/quota)
    }
  }, [location, geoItem, bookingFrom, bookingTo, adults, kids, dogs]);

  return h(LocationFormContext.Provider, {
    value: {
      location,
      setLocation,
      geoItem,
      setGeoItem,
      bookingFrom,
      setBookingFrom,
      bookingTo,
      setBookingTo,
      adults,
      setAdults,
      kids,
      setKids,
      dogs,
      setDogs,
      showAutocompleteOverlay,
      setShowAutocompleteOverlay,
      showGuestOverlay,
      setShowGuestOverlay,
      isValid,
    },
    children,
  });
}

export function useLocationFormStorage() {
  return useContext(LocationFormContext);
}
